import { Fragment, MouseEvent, useEffect, useState } from 'react';

import Menu from '~/src/common/components/Menu';
import { type Navigation } from '~/src/common/components/Navigation/types';

import { getCategoryPageLink, getSubcategoryAnchorLink } from '../utils';

interface Props {
  navigation: Navigation;
  isActiveID: (id: string) => boolean;
  onLinkClick: (id: string) => void;
}

const CategoryMenuClassic = ({ navigation, isActiveID, onLinkClick }: Props) => {
  // La famille et la catégorie en cours
  const family = navigation.families.find(item => isActiveID(item.id));
  const category = family?.categories.find(item => isActiveID(item.id));

  // La liste des catégories -- toutes familles confondues
  const categories = navigation.families.flatMap(fam => fam.categories);

  // Détecte si une catégorie appartient à la famille active
  const familyCategoryIDs = new Set(family?.categories.map(c => c.id));
  const isFromActiveFamily = (id: string) => familyCategoryIDs.has(id);

  // ID de l'élément ouvert et réouverture au changement
  const [expandedID, setExpandedID] = useState(category?.id);
  useEffect(() => setExpandedID(category?.id), [category?.id]);
  const handleCategoryClick = (item: { id: string }, event: MouseEvent) => {
    setExpandedID(item.id === expandedID ? undefined : item.id);
    if (item.id === category?.id) event.preventDefault();
    onLinkClick(item.id); // Tracking sur les catégories
  };

  // Tracking sur les sous-catégories
  const handleSubcategoryClick = (item: { id: string }) => {
    onLinkClick(item.id);
  };

  return (
    <>
      <Menu $mobile="primary" $desktop="primary">
        <Menu.Title $visibility="desktop">{family?.name}</Menu.Title>
        {categories.map(cat => (
          <Fragment key={cat.id}>
            <Menu.Entry
              key={cat.id}
              href={getCategoryPageLink(cat)}
              isActive={isActiveID(cat.id)}
              isExpanded={cat.id === expandedID}
              onClick={event => handleCategoryClick(cat, event)}
              $visibility={isFromActiveFamily(cat.id) ? 'all' : 'mobile'}>
              {cat.name}
            </Menu.Entry>
            {cat.id === category?.id && (
              <Menu $visibility="desktop" $mobile="secondary" $desktop="secondary">
                {category.children.map(sub => (
                  <Menu.Entry
                    key={sub.id}
                    isActive={isActiveID(sub.id)}
                    onClick={() => handleSubcategoryClick(sub)}
                    href={getSubcategoryAnchorLink(category, sub)}>
                    {sub.name}
                  </Menu.Entry>
                ))}
              </Menu>
            )}
          </Fragment>
        ))}
      </Menu>
      <Menu $mobile="secondary" $desktop="secondary" $visibility="mobile">
        {category?.children.map(sub => (
          <Menu.Entry
            key={sub.id}
            isActive={isActiveID(sub.id)}
            href={getSubcategoryAnchorLink(category, sub)}>
            {sub.name}
          </Menu.Entry>
        ))}
      </Menu>
    </>
  );
};

export default CategoryMenuClassic;
